import * as React from 'react';
import autobind from 'autobind-decorator';
import shortid from 'shortid';
import { PlusButton, MinusButton } from '../../../common/buttons';
import i18n from '../../../../R/string/i18n';
import R from '../../../../R';
import { SearchRoomCondition } from '../../../../models/search/SearchCondition';
import { Colors } from '../../../../shared/styles/StyleConst';

type Props = {
  rooms: SearchRoomCondition[];
  onChange: (rooms: SearchRoomCondition[]) => void;
  onChildModifyComplete: (isComplete: boolean) => void;
  isChildAgeChecked: boolean;
};

type State = {
  childAges: number[];
};

const MIN_NUM = 0;
const MAX_NUM = 5;

function isChildrenMinusButtonDisabled(rooms: SearchRoomCondition[], roomIndex: number) {
  const room = rooms[roomIndex];
  if (!room) return false;
  return room.childAges.length <= MIN_NUM;
}

function isChildrenPlusButtonDisabled(rooms: SearchRoomCondition[], roomIndex: number) {
  const room = rooms[roomIndex];
  if (!room) return false;
  return room.childAges.length >= MAX_NUM;
}

export default class ChildInput extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { rooms } = this.props;
    const index = 0;
    this.state = {
      childAges: rooms[index].childAges,
    };
  }

  @autobind
  onClickChildrenMinus(roomIndex: number) {
    const rooms = [...this.props.rooms];
    if (isChildrenMinusButtonDisabled(rooms, roomIndex)) return;
    rooms[roomIndex].childAges.pop();
    this.props.onChange(rooms);
    this.setState({
      childAges: rooms[roomIndex].childAges,
    });
    if (rooms[roomIndex].childAges.length < 1) {
      this.props.onChildModifyComplete(true);
    } else {
      this.props.onChildModifyComplete(rooms[roomIndex].childAges.every(e => e !== -1));
    }
  }

  @autobind
  onClickChildrenPlus(roomIndex: number) {
    const rooms = [...this.props.rooms];
    if (isChildrenPlusButtonDisabled(rooms, roomIndex)) return;
    rooms[roomIndex].childAges.push(-1);
    this.props.onChange(rooms);
    this.props.onChildModifyComplete(false);
    this.setState({
      childAges: rooms[roomIndex].childAges,
    });
  }

  @autobind
  onChangeChildAge(value: number, roomIndex: number, ageIndex: number) {
    const rooms = [...this.props.rooms];
    rooms[roomIndex].childAges[ageIndex] = value;
    this.props.onChange(rooms);
    this.setState({
      childAges: rooms[roomIndex].childAges,
    });
    this.props.onChildModifyComplete(rooms[roomIndex].childAges.every(e => e !== -1));
  }

  render() {
    const { rooms, isChildAgeChecked } = this.props;
    const { childAges } = this.state;
    // TODO: 複数部屋対応するまで0固定
    const roomIndex = 0;
    return (
      <React.Fragment>
        <div className="child-input row align-items-center">
          <div className="child-title col-4">
            <p>
              {i18n.yadoChildrenLabel({
                count: 1,
              })}
            </p>
            <p className="body-text">{i18n.yadoChildAgeDescriptionLabel()}</p>
          </div>
          <div className="col-8">
            <div className="d-flex justify-content-end align-items-center">
              <MinusButton
                onClick={() => this.onClickChildrenMinus(roomIndex)}
                disabled={isChildrenMinusButtonDisabled(rooms, roomIndex)}
              />
              <div className="number">{childAges.length}</div>
              <PlusButton
                onClick={() => this.onClickChildrenPlus(roomIndex)}
                disabled={isChildrenPlusButtonDisabled(rooms, roomIndex)}
              />
            </div>
          </div>
        </div>
        <div className="child-age-container container">
          {rooms[roomIndex].childAges.map((age, ageIndex) => (
            <div key={shortid()} className="child-age-input row align-items-center">
              <div className="child-age-title col-4">
                <p>
                  {i18n.webYadoSpecificChildAge({
                    key: ageIndex + 1,
                  })}
                </p>
              </div>
              <div className="child-age-value col-8">
                <select
                  onChange={e => this.onChangeChildAge(Number(e.target.value), roomIndex, ageIndex)}
                  value={age !== -1 ? age : ''}
                  className="ml-2"
                >
                  <option />
                  {Array(13)
                    .fill(0)
                    .map((v, i) => i)
                    .map(num => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                </select>
              </div>
              {age === -1 && isChildAgeChecked && (
                <div className="child-age-error">{i18n.webYadoChildAgeError()}</div>
              )}
            </div>
          ))}
        </div>
        <style jsx>
          {`
            .child-input {
              border-bottom: 1px solid #efefef;
              padding: 8px 0;
              width: 100%;
            }
            .child-input p {
              font-size: 14px;
              font-weight: 500;
              letter-spacing: 2.03px;
              text-align: left;
              color: ${Colors.deepBlack};
              margin-bottom: 0;
            }
            .child-input .body-text {
              color: #545454;
              font-size: 12px;
              font-weight: 400;
              line-height: 1.46429em;
              margin: 0;
            }
            .child-input .number {
              width: 70px;
              font-size: 14px;
              font-weight: bold;
              letter-spacing: 2.03px;
              text-align: center;
              color: ${Colors.deepBlack};
            }
            .child-title {
              padding-left: 0;
              white-space: nowrap;
            }
            .child-age-container .child-age-title {
              padding-left: 0;
              white-space: nowrap;
            }
            .child-age-input {
              height: 50px;
              border-bottom: 1px solid #efefef;
            }
            .child-age-input p {
              margin: 0;
              font-size: 13px;
              font-weight: 500;
              letter-spacing: 0.89px;
              color: #333333;
            }
            .child-age-input .child-age-value {
              display: flex;
              justify-content: flex-end;
            }
            .child-age-input .child-age-value select {
              background-color: #fff;
              background: none;
              padding: 0 0 0 0.75em !important;
              text-align: center !important;
              width: 100%;
              max-width: 130px;
              height: 32px;
              border-radius: 3px;
              border: solid 1px #2187f2;
              color: #2187f2;
              font-size: 16px;
              font-weight: 500;
              appearance: none;
              background: no-repeat url(${R.assets.images.commonSelectTriangles});
              background-position: right 5px top 7px;
            }
            .child-age-error {
              color: red;
              font-size: 12px;
            }
          `}
        </style>
      </React.Fragment>
    );
  }
}
